import { useFragment } from "@apollo/client/react";

import { UserFragment, UserFragmentDoc } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";

/**
 * Custom hook to retrieve a User's data from the cache.
 *
 * This hook uses the `useFragment` hook from Apollo Client to automatically
 * subscribe to changes in the User data.
 *
 * @param {string} [id] - Optional user ID. Falls back to the current user ID from the store.
 * @returns {UserFragment | null} The user data, or null if not found.
 */
export const useUserData = (id?: string): UserFragment | null => {
  // Always call the hook to maintain consistent hook order
  const userIdFromStore = useUserStore((state) => state.userId);
  // Use param, fallback to store value
  const userId = id ?? userIdFromStore;

  const { data } = useFragment({
    fragment: UserFragmentDoc,
    from: {
      __typename: "User",
      id: userId,
    },
  });

  if (!data) return null;

  return data as UserFragment;
};
